import React from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { motion } from "framer-motion";
import "./Footer.css"
// Futuristic Footer Component
const FuturisticFooter = () => {
  return (
    <div className="futuristic-footer-wrapper">
      {/* Floating Orbs Background */}
      <div className="orb-container">
        <div className="floating-orb orb-1"></div>
        <div className="floating-orb orb-2"></div>
        <div className="floating-orb orb-3"></div>
        <div className="grid-overlay"></div>
        <div className="scan-lines"></div>
      </div>

      <footer className="futuristic-footer">
        <div className="footer-container">
          <div className="footer-content">
            
            {/* Company Info - Blue Gradient 1 */}
            <motion.div 
              className="footer-section section-blue-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="section-glow glow-1"></div>
              <h4 className="company-title">
                Naught Venture
              </h4>
              <p className="company-description">
                We envision a digital-first future where boundaries between imagination and implementation dissolve. At Naught, we don't just adapt to change — we architect it.
              </p>
              <p className="company-tagline">
                Think Digital. Think Naught.
              </p>
              <div className="tag-container">
                <span className="futuristic-tag">🚀 Innovative</span>
                <span className="futuristic-tag">💡 Creative</span>
              </div>
            </motion.div>

            {/* Quick Links - Blue Gradient 2 */}
            <motion.div 
              className="footer-section section-blue-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="section-glow glow-2"></div>
              <h4 className="section-title">Quick Links</h4>
              <div className="links-container">
                <FooterLink to="/#services" label="Services" />
                <FooterLink to="/about" label="About Us" />
                <FooterLink to="/contact" label="Contact Us" />
              </div>
            </motion.div>

            {/* Contact Info - Blue Gradient 3 */}
            <motion.div 
              className="footer-section section-blue-3"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="section-glow glow-3"></div>
              <h4 className="section-title">Get in Touch</h4>
              <div className="contact-container">
                <ContactItem icon="envelope" label="sales@naught.co.in" link="mailto:sales@naught.co.in" />
                <ContactItem icon="phone" label="+917827856754" link="tel:+917827856754" />
                <ContactItem icon="map-marker-alt" label="H-141, Sector 63, Noida, U.P 201301" />
              </div>
              
              <div className="social-section">
                <h5 className="social-title">Follow Us</h5>
                <div className="social-container">
                  <SocialIcon href="https://www.linkedin.com/company/naught-ventures/" icon="linkedin-in" />
                  <SocialIcon href="https://www.instagram.com/naught_vc/" icon="instagram" />
                  <SocialIcon href="gmailto:sales@naught.co.in" icon="envelope" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Footer Bottom */}
          <motion.div 
            className="footer-bottom"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="footer-divider"></div>
            <p className="copyright-text">
              © {new Date().getFullYear()} <strong>Naught Venture Advisors Pvt Ltd.</strong> All rights reserved.
            </p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
};

export default FuturisticFooter;

// Reusable Footer Link Component
const FooterLink = ({ to, label }) => (
  <motion.div whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 300 }}>
    <Link
      to={to}
      className="futuristic-footer-link"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <span className="link-bullet"></span>
      <span className="link-text">{label}</span>
    </Link>
  </motion.div>
);

// Reusable Contact Item
const ContactItem = ({ icon, label, link }) => (
  <motion.div 
    className="contact-item"
    whileHover={{ x: 5 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <div className="contact-icon">
      <i className={`fas fa-${icon}`} />
    </div>
    {link ? (
      <a href={link} className="contact-link">{label}</a>
    ) : (
      <span className="contact-text">{label}</span>
    )}
  </motion.div>
);

// Reusable Social Icon
const SocialIcon = ({ href, icon }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="futuristic-social-icon"
    whileHover={{ scale: 1.2, y: -2 }}
    whileTap={{ scale: 0.9 }}
    transition={{ type: "spring", stiffness: 400, damping: 10 }}
  >
    <i className={`fab fa-${icon}`} />
  </motion.a>
);